import {lazy} from 'react';
import OneAppUserAPI from '@/user/user-oneapp-api';
import baseSchema from '../impersonation/schema';

const getImpersonationDetails = () => {
    return OneAppUserAPI.getImpersonationDetails() || {};
};

const isImpersonating = () => {
    return Boolean(getImpersonationDetails()?.impersonatorId);
};

const getImpersonatorType = () => {
    return getImpersonationDetails()?.impersonatorType;
};

// match a route against everything in the access list, and if no match is found,
// mark the route as inaccessible
const checkRouteAccessibility = (accessList, routePrefix, route) => {
    // this shouldn't happen, but we want to prevent non-route objects from being parsed
    if (!route?.id) {
        return false;
    }

    const matchingRouteFound = accessList.some(allowedItem => {
        const itemParts = allowedItem.split('.');

        // top-level route and top-level allow-list item
        if (itemParts.length === 1 && !routePrefix) {
            return itemParts[0] === route.id;
        }
        // sub-route and prefixed allow-list item where the parent route matches the item prefix
        else if (itemParts.length === 2 && itemParts[0] === routePrefix) {
            return itemParts[1] === route.id;
        }

        // any other combination is not worth comparing because they should never match
        return false;
    });

    if (!matchingRouteFound) {
        route.component = lazy(() => import('../router/Inaccessible'));

        return false;
    }

    return true;
};

// iterate through sub-route groups and check their impersonation accessibility.
// the code is only set up to handle groups that are formatted as arrays, not objects
const handleSubRouteGroups = (accessList, parentId, routeGroup) => {
    if (Array.isArray(routeGroup)) {
        routeGroup.forEach(subRoute =>
            checkRouteAccessibility(accessList, parentId, subRoute)
        );
    } else {
        // This is to warn the developer that they are doing something
        // that will break some existing functionality
        // eslint-disable-next-line no-console
        console.warn(
            `The route ${parentId} has a sub-route group that was created as an unparsable object instead of an array. 
            If this is supposed to be excluded from parsing, add it to the subGroupExclusionList so it will not be parsed.
            If this is supposed to be parsed, please change it to an array instead of an object`
        );
    }
};

// Impersonation route logic
const impersonationRouteLogic = (impersonatorType, routes) => {
    // Check if impersonatorType is valid and exists in the schema
    if (impersonatorType && baseSchema[impersonatorType]) {
        const accessList = baseSchema[impersonatorType];

        // Any time one route has a menu of routes that this logic is already
        // parsing elsewhere, we don't want to parse those routes again because
        // it could lead to a valid route being set to inaccessible
        const subGroupExclusionList = [
            'conditionManagement',
            'conditionManagementSidebar',
        ];

        // check each route against the allow list and mark it as inaccessible if there's no match
        routes.forEach(route => {
            const isAccessible = checkRouteAccessibility(accessList, '', route);

            if (!isAccessible || subGroupExclusionList.includes(route.id)) {
                return;
            }

            if (route.tabbedRoutes) {
                handleSubRouteGroups(accessList, route.id, route.tabbedRoutes);
            }

            if (route.routes) {
                handleSubRouteGroups(accessList, route.id, route.routes);
            }
        });
    }
};

const ImpersonationUtils = {
    getImpersonationDetails,
    isImpersonating,
    getImpersonatorType,
    impersonationRouteLogic,
};

export default ImpersonationUtils;
